export type CountryCode =
  | "US" | "GB" | "DE" | "GR" | "IT" | "ES" | "FR"
  | "AU" | "SG" | "AE" | "VN";

export type CurrencyCode = "USD" | "GBP" | "EUR" | "AUD" | "SGD" | "AED" | "VND";

export type Country = {
  code: CountryCode;
  name: string;
  flag: string;
  currency: CurrencyCode;
  symbol: string;
  locale: string;
};

/** Supported countries (currency is locked to each country). */
export const COUNTRIES: Country[] = [
  { code: "US", name: "United States",        flag: "🇺🇸", currency: "USD", symbol: "$",   locale: "en-US" },
  { code: "GB", name: "United Kingdom",       flag: "🇬🇧", currency: "GBP", symbol: "£",   locale: "en-GB" },
  { code: "DE", name: "Germany",              flag: "🇩🇪", currency: "EUR", symbol: "€",   locale: "de-DE" },
  { code: "FR", name: "France",               flag: "🇫🇷", currency: "EUR", symbol: "€",   locale: "fr-FR" },
  { code: "IT", name: "Italy",                flag: "🇮🇹", currency: "EUR", symbol: "€",   locale: "it-IT" },
  { code: "ES", name: "Spain",                flag: "🇪🇸", currency: "EUR", symbol: "€",   locale: "es-ES" },
  { code: "GR", name: "Greece",               flag: "🇬🇷", currency: "EUR", symbol: "€",   locale: "el-GR" },
  { code: "AU", name: "Australia",            flag: "🇦🇺", currency: "AUD", symbol: "A$",  locale: "en-AU" },
  { code: "SG", name: "Singapore",            flag: "🇸🇬", currency: "SGD", symbol: "S$",  locale: "en-SG" },
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", currency: "AED", symbol: "AED", locale: "en-AE" },
  { code: "VN", name: "Vietnam",              flag: "🇻🇳", currency: "VND", symbol: "₫",   locale: "vi-VN" },
];

export const DEFAULT_COUNTRY: Country = COUNTRIES[0];

export type Bundle = {
  qty: number;
  label: string;
  perCard: number;   // multiplier vs base unit price
  save?: number;     // percent
  badge?: string;
};

const STD_BUNDLES: Bundle[] = [
  { qty: 1,  label: "1 card",   perCard: 1.0 },
  { qty: 3,  label: "3 cards",  perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards",  perCard: 0.82, save: 18, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.72, save: 28, badge: "Best value" },
];

const GOOGLE_BUNDLES: Bundle[] = [
  { qty: 1,  label: "1 card",   perCard: 1.0 },
  { qty: 3,  label: "3 cards",  perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards",  perCard: 0.8,  save: 20, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.7,  save: 30, badge: "Best value" },
];

export type ProductPrices = Partial<Record<CountryCode, number>>;

export type Product = {
  slug: string;
  sku: string;
  name: string;
  shortName: string;
  href: string;
  prices: ProductPrices;
  bundles: Bundle[];
};

/**
 * Static product catalog (prices in major units of each currency).
 * US (USD) is the canonical static price used for the initial render so
 * pages never wait on client-side hydration to show a price.
 */
export const PRODUCTS: Product[] = [
  {
    slug: "metal", sku: "BC-METL", name: "Custom Metal AI Business Card", shortName: "Metal",
    href: "/shop/metal", bundles: STD_BUNDLES,
    prices: { US: 72, GB: 54, DE: 60, FR: 60, IT: 60, ES: 60, GR: 55, AU: 99, SG: 89, AE: 265, VN: 1450000 },
  },
  {
    slug: "bamboo", sku: "BC-BAMB", name: "Bamboo Smart Business Card", shortName: "Bamboo",
    href: "/shop/bamboo", bundles: STD_BUNDLES,
    prices: { US: 60, GB: 45, DE: 50, FR: 50, IT: 50, ES: 50, GR: 45, AU: 89, SG: 79, AE: 220, VN: 1100000 },
  },
  {
    slug: "white-pvc", sku: "BC-PVCW", name: "Custom White PVC AI Business Card", shortName: "White PVC",
    href: "/shop/white-pvc", bundles: STD_BUNDLES,
    prices: { US: 54, GB: 39, DE: 45, FR: 45, IT: 45, ES: 45, GR: 40, AU: 79, SG: 69, AE: 199, VN: 945000 },
  },
  {
    slug: "black-pvc", sku: "BC-PVCB", name: "Custom Black PVC AI Business Card", shortName: "Black PVC",
    href: "/shop/black-pvc", bundles: STD_BUNDLES,
    prices: { US: 54, GB: 39, DE: 45, FR: 45, IT: 45, ES: 45, GR: 40, AU: 79, SG: 69, AE: 199, VN: 945000 },
  },
  {
    slug: "24kt-gold", sku: "BC-AU24", name: "Custom 24kt Gold AI Business Card", shortName: "24kt Gold",
    href: "/shop/24kt-gold", bundles: STD_BUNDLES,
    prices: { US: 108, GB: 79, DE: 90, FR: 90, IT: 90, ES: 90, GR: 85, AU: 159, SG: 139, AE: 399, VN: 2350000 },
  },
  {
    slug: "google-review", sku: "GRC-PVC", name: "Google Review Cards", shortName: "Google Review",
    href: "/shop/google-review", bundles: GOOGLE_BUNDLES,
    prices: { US: 30, GB: 23, DE: 25, FR: 25, IT: 25, ES: 25, GR: 20, AU: 44, SG: 39, AE: 110, VN: 149000 },
  },
];

export const productBySlug = (slug: string) => PRODUCTS.find((p) => p.slug === slug);

export function getProductBasePrice(slug: string, country: CountryCode): number {
  const p = productBySlug(slug);
  if (!p) return 0;
  return p.prices[country] ?? p.prices.US ?? 0;
}

export function formatMoney(amount: number, country: Country): string {
  const isVND = country.currency === "VND";
  const rounded = isVND ? Math.round(amount) : Math.round(amount * 100) / 100;
  try {
    return new Intl.NumberFormat(country.locale, {
      style: "currency",
      currency: country.currency,
      minimumFractionDigits: isVND ? 0 : 2,
      maximumFractionDigits: isVND ? 0 : 2,
    }).format(rounded);
  } catch {
    return `${country.symbol}${rounded.toFixed(isVND ? 0 : 2)}`;
  }
}

/** Static USD formatter — used at module scope / SSR for fast render. */
export function formatUsd(amount: number): string {
  return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
}